import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Checkbox,
  DateInput,
  ErrorMessage,
  MessageBox,
  NavigationButton,
  Select,
} from "@jutro/ui";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { omit } from "radash";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import ReactSelect from "react-select";
import { adjustedToJutroUISelectStyles } from "components/PatientDocs/Ordered/utils";
import {
  DoctorGetPatientDocument,
  DoctorNfzHistoryDocument,
  DoctorPatientInsuranceStatusDocument,
  DoctorPatientProfileVisitDocument,
  useDoctorSaveHealthInsuranceStatementMutation,
} from "lib/graphql/megaSchema";
import { useVojevodshipOptions } from "lib/hooks/useVojevodshipOptions";
import { useTileSystem } from "lib/tools/createWorkspace/useTileSystem";
import { toaster } from "lib/tools/toaster";
import { useGetLoadPatientOptions } from "views/Patient/Profile/EwusStatement/hooks/useGetLoadPatientOptions";
import {
  ewusStatementDefaultFormValues,
  ewusStatementFormSchema,
  groupedRepresentativeOptions,
  groupedSelfOptions,
  representativeDataDefaultValues,
  statementTypeOptions,
} from "views/Patient/Profile/EwusStatement/tools";
import { EwusStatementForm } from "views/Patient/Profile/EwusStatement/types";

dayjs.extend(utc);

export const EwusStatement = () => {
  const { patientId } = useParams<{ patientId: string }>();

  const { handleSubmit, control, watch, reset, setValue } =
    useForm<EwusStatementForm>({
      mode: "onChange",
      resolver: zodResolver(ewusStatementFormSchema),
      defaultValues: ewusStatementDefaultFormValues,
    });

  const statementType = watch("statementType");

  const { removeTile } = useTileSystem();

  const loadPatientOptions = useGetLoadPatientOptions();

  const [
    saveHealthInsuranceStatementMutation,
    { loading: isSavingHealthInsurance },
  ] = useDoctorSaveHealthInsuranceStatementMutation({
    refetchQueries: [
      DoctorGetPatientDocument,
      DoctorPatientProfileVisitDocument,
      DoctorNfzHistoryDocument,
      DoctorPatientInsuranceStatusDocument,
    ],
    awaitRefetchQueries: true,
  });

  const { vojevodshipsWithCities } = useVojevodshipOptions();

  useEffect(() => {
    setValue("executionDate", dayjs().toISOString());
  }, [statementType]);

  const onSubmit = handleSubmit((data) => {
    if (!data.organizationId || !patientId) {
      return;
    }

    const representativeStatement =
      data.data.insuranceStatement.representativeStatement;

    if (representativeStatement?.representativePatient) {
      data.data.insuranceStatement.representativeStatement = {
        ...omit(representativeStatement, ["representativePatient"]),
        representativePatientId:
          representativeStatement.representativePatient.value,
      };
    }

    toaster.promise(
      saveHealthInsuranceStatementMutation({
        variables: {
          ...data,
          patientId,
          organizationId: data.organizationId,
        },
      }),
      {
        loading: "Zapisuję oświadczenie...",
        error: "Wystąpił błąd",
        success: () => {
          removeTile("patient-ewus-statement");

          return "Oświadczenie zostało zapisane";
        },
      },
    );
  });

  return (
    <div className="flex flex-col gap-4 rounded-lg bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="font-label">Oświadczenie pacjenta</div>
        <NavigationButton
          onClick={() => removeTile("patient-ewus-statement")}
        />
      </div>

      <form
        onSubmit={onSubmit}
        className="flex w-full flex-col justify-center gap-4"
      >
        <Controller
          name="statementType"
          control={control}
          render={({ field: { value: fieldValue, onChange, ...rest } }) => (
            <div className="relative">
              <a
                href="https://drive.google.com/drive/u/1/folders/16NScdXThQgsZ0CXVslK8ncdDVKKgQ1oo"
                target="_blank"
                rel="noreferrer"
                className="font-label absolute right-0 top-0 text-jutro-new-blue-800"
              >
                Zobacz wzory
              </a>
              <Select
                label="Rodzaj oświadczenia*"
                value={
                  statementTypeOptions.find(
                    ({ value }) => value === fieldValue,
                  ) ?? null
                }
                placeholder="Wybierz rodzaj oświadczenia z listy"
                options={statementTypeOptions}
                onChange={(option) => {
                  if (option?.value === "SELF") {
                    reset(ewusStatementDefaultFormValues);
                    return;
                  }

                  reset({
                    ...ewusStatementDefaultFormValues,
                    statementType: option?.value,
                    data: representativeDataDefaultValues,
                  });
                }}
                {...rest}
              />
            </div>
          )}
        />

        {statementType === "SELF" ? (
          <Controller
            name="data.insuranceStatement.patientStatement.reason"
            control={control}
            render={({
              field: { value: fieldValue, onChange, ...rest },
              fieldState: { error },
            }) => {
              const [polishGroup, ukrainianGroup] = groupedSelfOptions;

              const currentOption = [
                ...polishGroup.options,
                ...ukrainianGroup.options,
              ].find(({ value }) => value === fieldValue);

              return (
                <div className="flex w-full flex-col gap-1">
                  <label
                    htmlFor={rest.name}
                    className="font-label block w-full"
                  >
                    Podstawa prawna*
                  </label>
                  <ReactSelect
                    className="w-full"
                    placeholder="Wybierz podstawę prawną z listy"
                    value={currentOption}
                    options={groupedSelfOptions}
                    onChange={(option) =>
                      onChange(
                        option?.value ??
                          "OBJECIE_UBEZPIECZENIEM_ZDROWOTNYM_W_RZECZYPOSPOLITEJ_POLSKIEJ",
                      )
                    }
                    styles={adjustedToJutroUISelectStyles(error)}
                    {...rest}
                  />
                  <ErrorMessage error={error?.message} />
                </div>
              );
            }}
          />
        ) : (
          <div className="flex flex-col gap-4">
            <Controller
              name="data.insuranceStatement.representativeStatement.representativePatient"
              control={control}
              render={({
                field: { onChange, value, ...rest },
                fieldState: { error },
              }) => (
                <Select
                  {...rest}
                  isAsync
                  isSearchable
                  label={
                    statementTypeOptions.find(
                      (option) => option.value === "REPRESENTATIVE",
                    )?.label ?? ""
                  }
                  placeholder="Wpisz nazwisko / numer id / telefon / PESEL"
                  noOptionsMessage="Brak pacjentów"
                  loadOptions={loadPatientOptions}
                  onChange={(option) => {
                    onChange(option ?? null);
                  }}
                  value={value ?? null}
                  error={error?.message}
                />
              )}
            />

            <Controller
              name="data.insuranceStatement.representativeStatement.reason"
              control={control}
              render={({
                field: { value: fieldValue, onChange, ...rest },
                fieldState: { error },
              }) => {
                const [polishGroup, ukrainianGroup] =
                  groupedRepresentativeOptions;

                const currentOption = [
                  ...polishGroup.options,
                  ...ukrainianGroup.options,
                ].find(({ value }) => value === fieldValue);

                return (
                  <div className="flex w-full flex-col gap-1">
                    <label
                      htmlFor={rest.name}
                      className="font-label block w-full"
                    >
                      Podstawa prawna*
                    </label>
                    <ReactSelect
                      className="w-full"
                      value={currentOption}
                      placeholder="Wybierz podstawę prawną z listy"
                      options={groupedRepresentativeOptions}
                      onChange={(option) =>
                        onChange(
                          option?.value ??
                            "OBJECIE_UBEZPIECZENIEM_ZDROWOTNYM_W_RZECZYPOSPOLITEJ_POLSKIEJ",
                        )
                      }
                      styles={adjustedToJutroUISelectStyles(error)}
                      {...rest}
                    />
                    <ErrorMessage error={error?.message} />
                  </div>
                );
              }}
            />
          </div>
        )}

        <div className="flex gap-3">
          <Controller
            name="organizationId"
            control={control}
            defaultValue={null}
            render={({
              field: { value: fieldValue, onChange, ...rest },
              fieldState: { error },
            }) => (
              <Select
                label="Województwo*"
                value={
                  vojevodshipsWithCities.find(
                    ({ value }) => value === fieldValue,
                  ) ?? null
                }
                placeholder="Wybierz województwo z listy"
                options={vojevodshipsWithCities}
                onChange={(option) => onChange(option?.value ?? null)}
                error={error?.message}
                {...rest}
              />
            )}
          />

          <Controller
            name="executionDate"
            control={control}
            render={({
              field: { onChange, value, ...rest },
              fieldState: { error },
            }) => (
              <div>
                <DateInput
                  {...rest}
                  label="Data na oświadczeniu*"
                  value={value ? dayjs(value).toDate() : null}
                  onChange={(value) => {
                    if (!value) {
                      onChange(null);
                    }

                    const dateValue = dayjs(value as Date);

                    if (dateValue.isToday()) {
                      onChange(dayjs().toISOString());
                      return;
                    }

                    onChange(dateValue.startOf("day").utc().toISOString());
                  }}
                  maxDate={dayjs().toDate()}
                  onInvalidChange={() => onChange(null)}
                  error={error?.message}
                />
              </div>
            )}
          />
        </div>

        <Controller
          control={control}
          name="isStatementSigned"
          render={({
            field: { value, onChange, ...field },
            fieldState: { error },
          }) => (
            <Checkbox
              {...field}
              label="Oświadczenie zostało podpisane*"
              checked={value}
              onChange={(value) => onChange(value.target.checked)}
              error={error?.message}
            />
          )}
        />

        <MessageBox
          color="orange"
          text="Ważne: Pamiętaj by zeskanować oświadczenie i dodać je do modułu Informacje dodatkowe w profilu."
        />

        <Button text="Zapisz oświadczenie" loading={isSavingHealthInsurance} />
      </form>
    </div>
  );
};
